import { bytesToHex } from "./encoding";
import { bigintToFieldHex } from "./field";
import {
  PROVING_ARTIFACT_TIMEOUT_MS,
  readProvingResource,
} from "./readProvingResource";

export type CircuitName =
  | "reserve_deposit"
  | "reserve_spend"
  | "reserve_merge"
  | "control_register"
  | "control_action"
  | "create_subwallet";

export type ProvingArtifact = {
  wasmUrl: string;
  wasmSha256: string;
  provingKeyUrl: string;
  provingKeySha256: string;
};

export type ProvingManifest = Record<CircuitName, ProvingArtifact>;

export type Groth16Proof = {
  proofHex: string;
  publicInputs: string[];
};

type ProofCallData = [
  [string, string],
  [[string, string], [string, string]],
  [string, string],
  string[],
];

const expectedPublicInputs: Record<CircuitName, number> = {
  reserve_deposit: 4,
  reserve_spend: 7,
  reserve_merge: 5,
  control_register: 2,
  control_action: 5,
  create_subwallet: 5,
};

const artifactCache = new Map<string, Promise<string>>();

// The artifacts are served with no-store and weigh several MB each. A verified
// copy in Cache Storage (keyed by its SHA-256, checked again on every read)
// spares a page reload from downloading them again during an action.
const ARTIFACT_STORE = "rd-proving-artifacts-v1";
const storeKey = (url: string, sha256: string) =>
  `${new URL(url, globalThis.location?.href ?? "http://localhost/").pathname}?sha256=${sha256}`;

const storedArtifact = async (
  url: string,
  sha256: string
): Promise<Uint8Array | undefined> => {
  try {
    if (!globalThis.caches) return undefined;
    const hit = await (
      await globalThis.caches.open(ARTIFACT_STORE)
    ).match(storeKey(url, sha256));
    return hit ? new Uint8Array(await hit.arrayBuffer()) : undefined;
  } catch {
    return undefined;
  }
};

const storeArtifact = async (
  url: string,
  sha256: string,
  bytes: Uint8Array
): Promise<void> => {
  try {
    if (!globalThis.caches) return;
    await (
      await globalThis.caches.open(ARTIFACT_STORE)
    ).put(storeKey(url, sha256), new Response(bytes));
  } catch {
    // Storage full or unavailable: the next action downloads again.
  }
};

const sha256Hex = async (bytes: Uint8Array): Promise<string> =>
  bytesToHex(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)));

export const verifiedArtifactUrl = (
  url: string,
  expectedSha256: string
): Promise<string> => {
  const key = `${url}|${expectedSha256}`;
  const cached = artifactCache.get(key);
  if (cached) return cached;
  const result = (async () => {
    if (!/^[0-9a-f]{64}$/.test(expectedSha256)) {
      throw new Error("Proving artifact checksum is not canonical SHA-256");
    }
    const stored = await storedArtifact(url, expectedSha256);
    if (stored && (await sha256Hex(stored)) === expectedSha256)
      return URL.createObjectURL(new Blob([stored]));
    const bytes = new Uint8Array(
      await readProvingResource(
        (...args) => globalThis.fetch(...args),
        url,
        (response) => response.arrayBuffer(),
        PROVING_ARTIFACT_TIMEOUT_MS
      )
    );
    if ((await sha256Hex(bytes)) !== expectedSha256)
      throw new Error("Proving artifact checksum mismatch");
    void storeArtifact(url, expectedSha256, bytes);
    return URL.createObjectURL(new Blob([bytes]));
  })();
  artifactCache.set(key, result);
  void result.catch(() => {
    if (artifactCache.get(key) === result) artifactCache.delete(key);
  });
  return result;
};

const word = (value: string): string => bigintToFieldHex(BigInt(value));

export const generateV2Proof = async (params: {
  circuit: CircuitName;
  input: Record<string, unknown>;
  manifest: ProvingManifest;
}): Promise<Groth16Proof> => {
  const artifact = params.manifest[params.circuit];
  const [{ groth16 }, wasm, provingKey] = await Promise.all([
    import("snarkjs"),
    verifiedArtifactUrl(artifact.wasmUrl, artifact.wasmSha256),
    verifiedArtifactUrl(artifact.provingKeyUrl, artifact.provingKeySha256),
  ]);
  const { proof, publicSignals } = await groth16.fullProve(
    params.input as Record<string, string>,
    wasm,
    provingKey
  );
  if (publicSignals.length !== expectedPublicInputs[params.circuit]) {
    throw new Error("Circuit public-input ABI mismatch");
  }
  const callData = await groth16.exportSolidityCallData(proof, publicSignals);
  const parsed = JSON.parse(`[${callData}]`) as ProofCallData;
  const coordinates = [
    ...parsed[0],
    ...parsed[1][0],
    ...parsed[1][1],
    ...parsed[2],
  ];
  return {
    proofHex: coordinates.map(word).join(""),
    publicInputs: publicSignals.map(word),
  };
};

/**
 * Warm the proof system before the user acts: download (or load from Cache
 * Storage) and verify the circuits an action needs, and load snarkjs. Errors
 * are ignored here; the action retries and reports them itself.
 */
export const prefetchProvingArtifacts = async (
  manifest: Partial<ProvingManifest>,
  circuits: readonly CircuitName[]
): Promise<void> => {
  const artifacts = circuits.flatMap((circuit) => {
    const entry = manifest[circuit];
    return entry
      ? [
          [entry.wasmUrl, entry.wasmSha256],
          [entry.provingKeyUrl, entry.provingKeySha256],
        ]
      : [];
  });
  if (artifacts.length === 0) return;
  await Promise.allSettled([
    import("snarkjs"),
    ...artifacts.map(([url, sha256]) => verifiedArtifactUrl(url, sha256)),
  ]);
};
